import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useModule, useLessonsByModule, useCreateLesson, useUpdateLesson, useCourse } from '@/hooks'
import type { Lesson, CreateLessonDTO, UpdateLessonDTO, LessonContentType, LessonContentFormat, LessonStatus } from '@/types/lesson'
import { LessonFormModal } from '@/components/lessons/LessonFormModal'
import { QuizManager } from '@/components/quiz'
import { AssignmentManager } from '@/components/assignments'
import { lessonService as defaultLessonService } from '@/services/lessonService'

const STATUS_BADGES: Record<LessonStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-slate-400 text-white' },
  pending_review: { label: 'Pending Review', className: 'bg-blue-500 text-white' },
  approved: { label: 'Approved', className: 'bg-emerald-500 text-white' },
  rejected: { label: 'Rejected', className: 'bg-rose-500 text-white' },
  pending_delete: { label: 'Pending Delete', className: 'bg-orange-500 text-white' },
}

interface LessonFormData {
  title: string
  description: string
  contentType: LessonContentType
  contentUrl: string
  contentHtml: string
  contentFormat: LessonContentFormat
  durationMinutes: number
  isPreviewable: boolean
  isMandatory: boolean
}

export interface InstructorModuleEditorPageProps {
  // Injected in tests; in production defaults to the module-level lessonService
  // so existing callers keep working.
  lessonWorkflowService?: Pick<
    typeof defaultLessonService,
    'submitForReview' | 'requestDelete'
  >
}

export const InstructorModuleEditorPage: React.FC<InstructorModuleEditorPageProps> = ({
  lessonWorkflowService = defaultLessonService,
}) => {
  const { moduleId } = useParams<{ moduleId: string }>()
  const navigate = useNavigate()

  const [showLessonModal, setShowLessonModal] = useState(false)
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null)
  const [expandedLessonId, setExpandedLessonId] = useState<string | null>(null)
  const [workflowBusyId, setWorkflowBusyId] = useState<string | null>(null)
  const [workflowError, setWorkflowError] = useState<string | null>(null)

  // Fetch module and lessons
  const { data: module, isLoading: moduleLoading, error: moduleError } = useModule(moduleId || '')
  const { data: lessons, isLoading: lessonsLoading, refetch: refetchLessons } = useLessonsByModule(moduleId || '')

  // Fetch course info for breadcrumb
  const { data: course, isLoading: courseLoading } = useCourse(module?.courseId || '')

  // Mutations
  const createLesson = useCreateLesson()
  const updateLesson = useUpdateLesson()

  const isLoading = moduleLoading || lessonsLoading || courseLoading

  const handleSubmitForReview = async (lessonId: string) => {
    setWorkflowBusyId(lessonId)
    setWorkflowError(null)
    try {
      const res = await lessonWorkflowService.submitForReview(lessonId)
      if (!res.success) {
        throw new Error(res.error?.message || 'Failed to submit for review')
      }
      await refetchLessons()
    } catch (err) {
      setWorkflowError(err instanceof Error ? err.message : 'Failed to submit for review')
    } finally {
      setWorkflowBusyId(null)
    }
  }

  const handleRequestDelete = async (lessonId: string) => {
    if (!confirm('Request deletion of this lesson? An admin must approve.')) return
    setWorkflowBusyId(lessonId)
    setWorkflowError(null)
    try {
      const res = await lessonWorkflowService.requestDelete(lessonId)
      if (!res.success) {
        throw new Error(res.error?.message || 'Failed to request delete')
      }
      await refetchLessons()
    } catch (err) {
      setWorkflowError(err instanceof Error ? err.message : 'Failed to request delete')
    } finally {
      setWorkflowBusyId(null)
    }
  }

  const handleAddLesson = () => {
    setEditingLesson(null)
    setShowLessonModal(true)
  }

  const handleEditLesson = (lesson: Lesson) => {
    setEditingLesson(lesson)
    setShowLessonModal(true)
  }

  const handleSaveLesson = async (data: LessonFormData) => {
    try {
      if (editingLesson) {
        const updateData: UpdateLessonDTO = {
          title: data.title,
          description: data.description,
          contentType: data.contentType,
          contentUrl: data.contentUrl,
          contentHtml: data.contentHtml,
          contentFormat: data.contentFormat,
          durationMinutes: data.durationMinutes,
          isPreviewable: data.isPreviewable,
          isMandatory: data.isMandatory,
        }
        await updateLesson.mutateAsync({
          id: editingLesson.id,
          data: updateData,
        })
      } else {
        const createData: CreateLessonDTO = {
          moduleId: moduleId || '',
          title: data.title,
          description: data.description,
          contentType: data.contentType,
          contentUrl: data.contentUrl,
          contentHtml: data.contentHtml,
          contentFormat: data.contentFormat,
          durationMinutes: data.durationMinutes,
          orderIndex: (lessons?.length || 0),
          isPreviewable: data.isPreviewable,
          isMandatory: data.isMandatory,
        }
        await createLesson.mutateAsync(createData)
      }
      refetchLessons()
      setShowLessonModal(false)
    } catch (error) {
      console.error('Failed to save lesson:', error)
    }
  }

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return 'play_circle'
      case 'pdf':
        return 'picture_as_pdf'
      case 'text':
        return 'article'
      default:
        return 'description'
    }
  }

  const getContentTypeColor = (type: string) => {
    switch (type) {
      case 'video':
        return 'bg-blue-500 text-white'
      case 'pdf':
        return 'bg-rose-500 text-white'
      case 'text':
        return 'bg-emerald-500 text-white'
      default:
        return 'bg-slate-400 text-white'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin inline-block size-8 border-4 border-slate-100 border-t-primary rounded-full mb-4" />
          <p className="text-sm font-bold text-slate-500">Memuat modul...</p>
        </div>
      </div>
    )
  }

  if (moduleError) {
    return (
      <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-6 text-center">
        <span className="material-symbols-outlined text-red-400 text-4xl mb-3">error</span>
        <p className="text-red-500 font-bold text-lg">Gagal memuat modul</p>
        <p className="text-red-400/70 text-sm mt-2">{moduleError.message}</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-300 text-sm font-bold hover:bg-slate-900 dark:hover:bg-white hover:text-white dark:hover:text-slate-900 transition-all"
        >
          Kembali
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="pb-8 mb-8 border-b border-slate-200 dark:border-white/10">
        <PageHeader
          title={module?.title || 'Module Editor'}
          subtitle="Edit module content and lessons"
          breadcrumbs={[
            { label: 'My Courses', to: '/instructor/courses' },
            { label: course?.title || 'Course', to: `/instructor/courses/${module?.courseId}/modules` },
            { label: module?.title || 'Module' }
          ]}
        />
      </div>

      <div className="px-1">
        {/* Module Info Card */}
        <div className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-200 dark:border-white/10 p-6 mb-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span
                  className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${
                    module?.isDraft
                      ? 'bg-slate-400 text-white'
                      : 'bg-emerald-500 text-white'
                  }`}
                >
                  {module?.isDraft ? 'Draft' : 'Published'}
                </span>
              </div>
              <h2 className="text-xl font-black text-slate-900 dark:text-white mb-1 tracking-tight">{module?.title}</h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm">{module?.description || 'Tidak ada deskripsi'}</p>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[16px]">schedule</span>
                <span className="font-bold">{module?.durationHours || 0}j</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[16px]">play_circle</span>
                <span className="font-bold">{lessons?.length || 0} materi</span>
              </div>
            </div>
          </div>
        </div>

        {/* Lessons Section */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">Materi</h3>
          <button
            onClick={handleAddLesson}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg shadow-slate-200 dark:shadow-none"
          >
            <span className="material-symbols-outlined text-[18px]">add_circle</span>
            Tambah Materi
          </button>
        </div>

        {workflowError && (
          <div
            role="alert"
            data-testid="workflow-error"
            className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm font-bold"
          >
            {workflowError}
          </div>
        )}

        {/* Lessons List */}
        {!lessons || lessons.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-200 dark:border-white/10 border-dashed p-16 text-center shadow-sm">
            <div className="size-16 rounded-full bg-slate-100 dark:bg-white/5 mx-auto mb-4 flex items-center justify-center border border-slate-200 dark:border-white/10">
              <span className="material-symbols-outlined text-[32px] text-slate-400">add_circle</span>
            </div>
            <p className="text-slate-900 dark:text-white font-black mb-1">Belum ada materi</p>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">Tambahkan materi pertama ke modul ini</p>
            <button
              onClick={handleAddLesson}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg shadow-slate-200 dark:shadow-none"
            >
              <span className="material-symbols-outlined text-[18px]">add_circle</span>
              Tambah Materi Pertama
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
            <div className="divide-y divide-slate-100 dark:divide-white/5">
              {lessons.map((lesson: Lesson, index: number) => {
                const isExpanded = expandedLessonId === lesson.id
                return (
                  <div key={lesson.id}>
                    <div className={`px-6 py-4 transition-all ${isExpanded ? 'bg-primary/[0.02]' : 'hover:bg-slate-50/50 dark:hover:bg-white/[0.01]'}`}>
                      <div className="flex items-center gap-4">
                        {/* Order Number */}
                        <div className="size-9 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center text-xs font-black text-slate-500 dark:text-slate-400 shrink-0">
                          {index + 1}
                        </div>

                        {/* Content Type Icon */}
                        <div
                          className={`size-10 rounded-xl flex items-center justify-center shrink-0 ${getContentTypeColor(
                            lesson.contentType
                          )}`}
                        >
                          <span className="material-symbols-outlined text-[20px]">
                            {getContentTypeIcon(lesson.contentType)}
                          </span>
                        </div>

                        {/* Lesson Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <h4 className="text-sm font-black text-slate-900 dark:text-white truncate tracking-tight">{lesson.title}</h4>
                            {lesson.lessonStatus && (() => {
                              const badge = STATUS_BADGES[lesson.lessonStatus] ?? STATUS_BADGES.draft
                              return (
                                <span
                                  data-testid={`lesson-status-${lesson.id}`}
                                  className={`px-2.5 py-1 rounded-full text-xs font-bold ${badge.className}`}
                                >
                                  {badge.label}
                                </span>
                              )
                            })()}
                            {lesson.isPreviewable && (
                              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500 text-white">
                                Preview
                              </span>
                            )}
                            {lesson.isMandatory && (
                              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-orange-500 text-white">
                                Wajib
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {lesson.contentType.toUpperCase()} - {lesson.durationMinutes} menit
                          </p>
                          {lesson.lessonStatus === 'rejected' && lesson.reviewNote && (
                            <div
                              role="alert"
                              data-testid={`rejection-banner-${lesson.id}`}
                              className="mt-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-[12px] text-red-500 font-medium"
                            >
                              <strong>Ditolak:</strong> {lesson.reviewNote}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                          {(lesson.lessonStatus === 'draft' || lesson.lessonStatus === 'rejected' || !lesson.lessonStatus) && (
                            <button
                              onClick={() => handleSubmitForReview(lesson.id)}
                              disabled={workflowBusyId === lesson.id}
                              data-testid={`submit-review-${lesson.id}`}
                              className="px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-500 border border-blue-500/20 text-xs font-bold hover:bg-blue-500 hover:text-white transition-all disabled:opacity-50"
                            >
                              Submit Review
                            </button>
                          )}
                          {lesson.lessonStatus === 'approved' && (
                            <button
                              onClick={() => handleRequestDelete(lesson.id)}
                              disabled={workflowBusyId === lesson.id}
                              data-testid={`request-delete-${lesson.id}`}
                              className="px-3 py-1.5 rounded-lg bg-orange-500/10 text-orange-500 border border-orange-500/20 text-xs font-bold hover:bg-orange-500 hover:text-white transition-all disabled:opacity-50"
                            >
                              Hapus
                            </button>
                          )}
                          <button
                            onClick={() => setExpandedLessonId(isExpanded ? null : lesson.id)}
                            className={`p-2 rounded-xl transition-all ${
                              isExpanded
                                ? 'bg-primary/10 text-primary border border-primary/20'
                                : 'bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 hover:text-primary hover:bg-primary/10 border border-transparent hover:border-primary/20'
                            }`}
                            title="Quiz & Tugas"
                          >
                            <span className="material-symbols-outlined text-[18px]">quiz</span>
                          </button>
                          <button
                            onClick={() => handleEditLesson(lesson)}
                            disabled={lesson.lessonStatus === 'pending_review'}
                            data-testid={`edit-${lesson.id}`}
                            className="p-2 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-white/10 transition-all border border-transparent disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Edit materi"
                          >
                            <span className="material-symbols-outlined text-[18px]">edit</span>
                          </button>
                          <button
                            onClick={() => handleRequestDelete(lesson.id)}
                            disabled={workflowBusyId === lesson.id}
                            className="p-2 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 hover:text-red-500 hover:bg-red-500/10 transition-all border border-transparent hover:border-red-500/20 disabled:opacity-50"
                            title="Hapus materi"
                          >
                            <span className="material-symbols-outlined text-[18px]">
                              {workflowBusyId === lesson.id ? 'hourglass_empty' : 'delete'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Activities Section */}
                    {isExpanded && (
                      <div className="border-t border-slate-200 dark:border-white/10 px-6 py-5 bg-slate-50/50 dark:bg-white/[0.02]">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="material-symbols-outlined text-primary text-[20px]">extension</span>
                          <h4 className="text-sm font-black text-slate-900 dark:text-white tracking-tight">Aktivitas untuk "{lesson.title}"</h4>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* Quiz Manager */}
                          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-white/10 p-4">
                            <h5 className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                              <span className="material-symbols-outlined text-[14px]">quiz</span>
                              Kuis
                            </h5>
                            <QuizManager lessonId={lesson.id} lessonTitle={lesson.title} />
                          </div>
                          {/* Assignment Manager */}
                          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-white/10 p-4">
                            <h5 className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                              <span className="material-symbols-outlined text-[14px]">assignment</span>
                              Tugas
                            </h5>
                            <AssignmentManager lessonId={lesson.id} lessonTitle={lesson.title} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Info Card */}
        <div className="mt-6 bg-white dark:bg-slate-900 rounded-[24px] border border-slate-200 dark:border-white/10 p-5 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="size-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-amber-500 text-[20px]">
                lightbulb
              </span>
            </div>
            <div>
              <h4 className="text-slate-900 dark:text-white font-black mb-1 text-sm">Tips Editing</h4>
              <ul className="text-slate-500 dark:text-slate-400 text-xs space-y-1">
                <li>- Upload video langsung atau paste link YouTube/Vimeo</li>
                <li>- Tandai materi sebagai "Preview" untuk akses gratis</li>
                <li>- Perubahan disimpan sebagai draft sampai admin publish</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Lesson Form Modal */}
      {showLessonModal && (
        <LessonFormModal
          lesson={editingLesson}
          onSave={handleSaveLesson}
          onClose={() => setShowLessonModal(false)}
          isSaving={createLesson.isPending || updateLesson.isPending}
        />
      )}
    </div>
  )
}

export default InstructorModuleEditorPage
